import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getStaff, createStaff, updateStaff, deleteStaff } from '../services/staffApi';
import { Staff, CreateStaffRequest } from '../types/Staff';
import './StaffList.css';

const StaffList: React.FC = () => {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateStaffRequest>({
    email: '',
    name: '',
  });

  const loadStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      const data = await getStaff(token);
      setStaff(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load staff');
      console.error('Error loading staff:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const handleAdd = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await createStaff(token, formData);
      setFormData({ email: '', name: '' });
      setShowAddForm(false);
      loadStaff();
    } catch (err: any) {
      alert('Error creating staff: ' + err.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingStaff) return;
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await updateStaff(token, editingStaff.email, {
        name: formData.name,
      });
      setEditingStaff(null);
      setFormData({ email: '', name: '' });
      loadStaff();
    } catch (err: any) {
      alert('Error updating staff: ' + err.message);
    }
  };

  const handleDelete = async (email: string) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      await deleteStaff(token, email);
      loadStaff();
    } catch (err: any) {
      alert('Error deleting staff: ' + err.message);
    }
  };

  const startEdit = (staffMember: Staff) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email,
      name: staffMember.name,
    });
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingStaff(null);
    setFormData({ email: '', name: '' });
  };

  if (loading) {
    return <div className="loading">Loading staff...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>Error: {error}</p>
        <button onClick={loadStaff}>Retry</button>
      </div>
    );
  }

  return (
    <div className="staff-container">
      <div className="page-header">
        <h2>Staff Management</h2>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingStaff(null);
          }}
          className="add-button"
        >
          {showAddForm ? 'Cancel' : '+ Add Staff'}
        </button>
      </div>

      {(showAddForm || editingStaff) && (
        <div className="staff-form">
          <h3>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editingStaff}
            />
          </div>
          <div className="form-group">
            <label>Name:</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className="form-actions">
            {editingStaff ? (
              <>
                <button onClick={handleUpdate} className="save-button">
                  Update
                </button>
                <button onClick={cancelEdit} className="cancel-button">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={handleAdd} className="save-button">
                  Create
                </button>
                <button onClick={() => setShowAddForm(false)} className="cancel-button">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="staff-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((staffMember) => (
              <tr key={staffMember.email}>
                <td>{staffMember.name}</td>
                <td>{staffMember.email}</td>
                <td>
                  <button onClick={() => startEdit(staffMember)} className="edit-button">
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(staffMember.email)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {staff.length === 0 && (
          <div className="no-staff">No staff members found. Add one to get started!</div>
        )}
      </div>
    </div>
  );
};

export default StaffList;
